import React, { memo, useCallback, useMemo } from 'react'
import tw from 'twin.macro'
import { useSelector } from 'react-redux'
import type { GridRowParams } from '@mui/x-data-grid-pro'
import { GridTable } from 'src/components/shared/grid/GridTable'
import { InternalPermission } from 'src/constants/internal-access/internalPermissions'
import { useHasPermission } from 'src/hooks/useHasPermission'
import type { InternalAccessRole } from 'src/interfaces/internalAccess'
import {
  getInternalRoles,
  getInternalRolesLoading,
  getInternalUsers,
} from 'src/selectors/internalAccess'
import { countUsersByRole } from 'src/utils/internalAccess/internalUsers'
import { EmptyInternalRoles } from './EmptyInternalRoles'
import { buildInternalRoleColumns } from './internalRoleColumns'

interface Props {
  onRowClick(role: InternalAccessRole): void
}

const getRowId = (row: InternalAccessRole): string => {
  return row.id
}

const InternalRolesTableComponent = ({ onRowClick }: Props) => {
  const roles = useSelector(getInternalRoles)
  const users = useSelector(getInternalUsers)
  const isLoading = useSelector(getInternalRolesLoading)
  const canViewUsers = useHasPermission(InternalPermission.InternalUsersView)

  const userCounts = useMemo(() => {
    if (!canViewUsers) {
      return null
    }

    return countUsersByRole(users)
  }, [canViewUsers, users])

  const columns = useMemo(() => {
    return buildInternalRoleColumns(userCounts)
  }, [userCounts])

  const handleRowClick = useCallback(
    (params: GridRowParams<InternalAccessRole>) => {
      onRowClick(params.row)
    },
    [onRowClick],
  )

  return (
    <GridTable<InternalAccessRole>
      rows={roles}
      columns={columns}
      getRowId={getRowId}
      isLoading={isLoading}
      sortingMode="client"
      fillHeight
      EmptyElement={EmptyInternalRoles}
      className={tw`[& .MuiDataGrid-row]:cursor-pointer`}
      onRowClick={handleRowClick}
      tableDataTestId="internal-roles-table"
      rowDataTestIdPrefix="internal-roles"
    />
  )
}

export const InternalRolesTable = memo(InternalRolesTableComponent)
